import { IImageGalleryBlock } from '@models/IImageGalleryBlock';
import { ILightBoxImage } from '@models/ILightBoxImage';
import ImageMasonry from '@molecules/ImageMasonry/ImageMasonry';
import ListingResultsContainer from '@molecules/ListingResultsContainer/ListingResultsContainer';
import LoadMoreListing from '@molecules/LoadMoreListing/LoadMoreListing';
import ContentFilters from '@organisms/ContentFilters/ContentFilters';
import useListingComponentProps from '@utils/hooks/useListingComponentProps';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

const ImageGalleryBlock = (props: IImageGalleryBlock) => {
  const {
    anchorId = '',
    componentName,
    endpoint,
    galleryId,
    imageCountText,
    showExpandedFilters,
    liveFeed,
    liveFeedLabel,
    sectionTitle,
    showFacets,
    showTotalImages,
    title,
  } = props;

  const persistingParams = { galleryId: [galleryId] };

  const { results, ...listingProps } = useListingComponentProps({
    ...props,
    endpoint: `/${endpoint}`,
    persistingParams,
  });

  if (!results?.length) return null;

  return (
    <section
      id={`a-${anchorId}`}
      data-name={componentName}
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
    >
      <div className="container grid grid-cols-1 items-center pt-8 pb-4 lg:grid-cols-12 lg:pb-8">
        <div className="col-span-full flex flex-wrap justify-between gap-x-4 gap-y-5 lg:gap-x-7 lg:gap-y-7.5">
          {sectionTitle && (
            <h3 className="w-full" data-testid="CT-08-Section-Title">
              {sectionTitle}
            </h3>
          )}
          {showTotalImages && (
            <div className="flex flex-col lg:flex-row lg:gap-x-7">
              <AnimatePresence>
                {!listingProps.loading && (
                  <motion.div
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    {imageCountText && (
                      <p className="h6">
                        <span>{listingProps.totalNumberResults || 0}</span>
                        <span className="ml-2">{imageCountText}</span>
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {!showTotalImages && (
            <div className="flex shrink-0 flex-col lg:flex-row lg:gap-x-7">
              <h3
                className="body-one text-black-100 dark:text-white"
                data-testid="CT-08-Title"
              >
                {title}
              </h3>
              {liveFeed && liveFeedLabel && (
                <span
                  className="body-one ml-0 flex items-center font-light text-grey-450 dark:text-grey-500"
                  data-testid="CT-08-Live-Feed-Label"
                >
                  {liveFeedLabel}
                  <span
                    className={classNames(
                      'ml-2 flex h-4 w-4 items-center justify-center rounded-full border',
                      liveFeed === 'active' ? 'border-green' : 'border-gray-500'
                    )}
                    data-testid="CT-08-Live-Feed-Status"
                  >
                    <span
                      className={classNames(
                        'h-2 w-2 rounded-full',
                        liveFeed === 'active' ? 'bg-green' : 'bg-grey-500'
                      )}
                    />
                  </span>
                </span>
              )}
            </div>
          )}
          <div className="flex items-end justify-end">
            {showFacets && (
              <ContentFilters
                {...listingProps}
                showExpandedFilters={showExpandedFilters}
              />
            )}
          </div>
        </div>
      </div>

      <ListingResultsContainer
        results={results}
        {...listingProps}
        noResultsClassName="container"
        errorClassName="container"
      >
        <ImageMasonry images={results as ILightBoxImage[]} {...listingProps} />
      </ListingResultsContainer>

      {results?.length > 0 && results.length < listingProps.total && (
        <div className="container grid-cols-1 pt-8 lg:grid lg:grid-cols-12">
          <div className="col-span-4 col-start-1 py-4 text-lg">
            <LoadMoreListing
              results={results as ILightBoxImage[]}
              {...listingProps}
              pageSize={3}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default ImageGalleryBlock;
